/**
 * Two balanced two-way teams (slice 9) for full games: the harness's section 5 and rotation check,
 * the multi-roster sanity bands, and the client's "Play a game". The Play 1 rosters stay for the
 * pinned Play 1 tests and the play matrix.
 *
 * How they were chosen (a one-off search over the creature pack, checked by pack.balanced.test.ts):
 * 1. Gen 1 only, since only Gen 1 (and Steelix) sprite sheets are committed for the client.
 * 2. Seven two-way starter roles, paired the way the Play 1 charts pair them (QB/S, OL1/DL1,
 *    OL2/DL2, WR1/CB1, WR2/CB2, RB/LB1, TE/LB2), and four bench roles (line, line, WR/CB, RB/LB).
 * 3. For each role, pairs of different species with the same typing, so every matchup across the
 *    two teams is a type mirror, each passing its role's base-stat screen (balance.json
 *    `positionFit.listing`, the one source since slice 11; packages/data `listedPositions`, whose
 *    test checks every pair below against it); base-stat totals within 45 of each other.
 * 4. Over one pair per role (the 7 best-matched pairs a role, both orientations, no species
 *    twice), keep the rosters whose 11-Pokémon base-stat totals are exactly equal (117,504 of
 *    them), and take the one with the least 2 x starters' total gap + the sum of per-pair total
 *    gaps + the sum of per-pair role-score gaps (role score: QB/S Sp. Atk + Spe; line Atk + Def;
 *    WR/CB 2 Spe + Sp. Def; RB/LB Atk + Spe + Def/2; TE/LB (Atk + Def + Sp. Def + Spe)/2).
 *    Result: 5,285 each over 11; starters 3,422 to 3,420.
 * 5. Each Pokémon is listed for its role's two positions (`positions`), so a starter is always in
 *    position and a bench Pokémon is in position only in its role. Level 50, no moves, abilities,
 *    items, or bag: the scripted coach spends nothing, and an ability would make one side's
 *    rotation stronger than the other's.
 * Known limit: type mirrors make games between these two close to type-less; "It's super
 * effective!" rarely fires. Real teams come with slice 11's roster picker.
 */
import { mon } from './sample-drive';
import type { Combatant, Move, Position, Team, TeamId, TypeName } from '../../src/types';

type Six = [number, number, number, number, number, number];

function listed(
  name: string,
  types: [TypeName] | [TypeName, TypeName],
  stats: Six,
  positions: [Position, Position],
): Combatant {
  return { ...mon(name, types, stats, positions[0]), positions };
}

const QB_S: [Position, Position] = ['QB', 'S'];
const LINE: [Position, Position] = ['OL', 'DL'];
const WR_CB: [Position, Position] = ['WR', 'CB'];
const RB_LB: [Position, Position] = ['RB', 'LB'];
const TE_LB: [Position, Position] = ['TE', 'LB'];

/** Roster order: QB/S, OL1/DL1, OL2/DL2, WR1/CB1, WR2/CB2, RB/LB1, TE/LB2, then the bench. */
const offense = { QB: 0, OL1: 1, OL2: 2, WR1: 3, WR2: 4, RB: 5, TE: 6 };
const defense = { S: 0, DL1: 1, DL2: 2, CB1: 3, CB2: 4, LB1: 5, LB2: 6 };

export function balancedTeams(): Record<TeamId, Team> {
  return {
    A: {
      id: 'A',
      name: 'Team Electabuzz',
      roster: [
        listed('Electabuzz', ['Electric'], [65, 83, 57, 95, 85, 105], QB_S),
        listed('Rhydon', ['Ground', 'Rock'], [105, 130, 120, 45, 45, 40], LINE),
        listed('Arcanine', ['Fire'], [90, 110, 80, 100, 80, 95], LINE),
        listed('Ninetales', ['Fire'], [73, 76, 75, 81, 100, 100], WR_CB),
        listed('Fearow', ['Normal', 'Flying'], [65, 90, 65, 61, 61, 100], WR_CB),
        listed('Hitmonlee', ['Fighting'], [50, 120, 53, 35, 110, 87], RB_LB),
        listed('Victreebel', ['Grass', 'Poison'], [80, 105, 65, 100, 70, 70], TE_LB),
        // Bench: line, line, WR/CB, RB/LB.
        listed('Kabutops', ['Rock', 'Water'], [60, 115, 105, 65, 70, 80], LINE),
        listed('Sandslash', ['Ground'], [75, 100, 110, 45, 55, 65], LINE),
        listed('Raticate', ['Normal'], [55, 81, 60, 50, 70, 97], WR_CB),
        listed('Nidoking', ['Poison', 'Ground'], [81, 102, 77, 85, 75, 85], RB_LB),
      ],
      offense: { ...offense },
      defense: { ...defense },
    },
    B: {
      id: 'B',
      name: 'Team Raichu',
      roster: [
        listed('Raichu', ['Electric'], [60, 90, 55, 90, 80, 110], QB_S),
        listed('Golem', ['Rock', 'Ground'], [80, 120, 130, 55, 65, 45], LINE),
        listed('Flareon', ['Fire'], [65, 130, 60, 95, 110, 65], LINE),
        listed('Rapidash', ['Fire'], [65, 100, 70, 80, 80, 105], WR_CB),
        listed('Dodrio', ['Normal', 'Flying'], [60, 110, 70, 60, 60, 110], WR_CB),
        listed('Primeape', ['Fighting'], [65, 105, 60, 60, 70, 95], RB_LB),
        listed('Vileplume', ['Grass', 'Poison'], [75, 80, 85, 110, 90, 50], TE_LB),
        listed('Omastar', ['Rock', 'Water'], [70, 60, 125, 115, 70, 55], LINE),
        listed('Marowak', ['Ground'], [60, 80, 110, 50, 80, 45], LINE),
        listed('Persian', ['Normal'], [65, 70, 60, 65, 65, 115], WR_CB),
        listed('Nidoqueen', ['Poison', 'Ground'], [90, 92, 87, 75, 85, 76], RB_LB),
      ],
      offense: { ...offense },
      defense: { ...defense },
    },
  };
}

/**
 * The kitted teams (slice 7): Quick Play's two presets, the AI's harness section 6, and its pinned
 * game. Since slice 10a each preset's eleven (`PRESET_SPECIES`) is built the way the roster screen
 * builds a player's team (`@tdmons/data/roster-draft`: picked in roster order, then Recommend):
 * both depth charts proposed from the slot grades (`KITTED_CHARTS`), each Pokémon listed for the
 * positions its base stats pass (`LISTED`), the move set `draftMoveset` drafts for its role in those
 * charts (`KIT`), PP full, and the recommended three-item bag. Only the names stay the presets'.
 * The hand-set charts lost 71-80% to the screen's build of their own species (docs/decisions.md).
 * pack.kitted.test.ts checks the whole team against a fresh build. No held items or abilities.
 * Plain `balancedTeams()` keeps section 5, 5b, and their pins comparable with slice 9. Team
 * Electabuzz is plain team A's eleven. Team Raichu was re-picked in slice 10b and again in 10c
 * (tools/harness statmatch): its 11-man Atk, Def, Sp. Atk and Spe each within 3% of Team
 * Electabuzz's, an even game against it with types off, Counter's type lift against it within +5
 * of Counter's against Team Electabuzz, then the lowest exposure to one attacking type (harness
 * coverage.ts) and Casual's lowest lift. It shares Raticate with Team Electabuzz.
 */
export function kittedBalancedTeams(): Record<TeamId, Team> {
  const teams = balancedTeams();
  teams.A.roster = presetRoster('electabuzz');
  teams.B.roster = presetRoster('raichu');
  const kit = (c: Combatant): Combatant => {
    const moves = (KIT[c.creatureId] ?? []).map((id) => KIT_MOVES[id] as Move);
    const positions = LISTED[c.creatureId] ?? [];
    return {
      ...c,
      position: positions[0] ?? 'BENCH',
      positions: [...positions],
      moves,
      ppLeft: Object.fromEntries(moves.map((m) => [m.id, m.pp])),
    };
  };
  for (const t of Object.values(teams)) {
    t.roster = t.roster.map(kit);
    t.offense = { ...KITTED_CHARTS[t.id].offense };
    t.defense = { ...KITTED_CHARTS[t.id].defense };
    t.bag = [...KIT_BAG];
  }
  return teams;
}

/** Recommend's depth charts for each kitted team, as indexes into its roster order. */
export const KITTED_CHARTS: Record<TeamId, Pick<Team, 'offense' | 'defense'>> = {
  A: {
    offense: { QB: 2, RB: 7, WR1: 0, WR2: 3, TE: 4, OL1: 1, OL2: 8 },
    defense: { DL1: 1, DL2: 7, LB1: 5, LB2: 4, CB1: 3, CB2: 9, S: 0 },
  },
  B: {
    offense: { QB: 9, RB: 3, WR1: 0, WR2: 5, TE: 10, OL1: 1, OL2: 6 },
    defense: { DL1: 2, DL2: 3, LB1: 6, LB2: 5, CB1: 4, CB2: 10, S: 0 },
  },
};

/** The positions each species' base stats list it for (`listedPositions`), by creature id. */
export const LISTED: Record<string, Position[]> = {
  electabuzz: ['QB', 'S', 'WR', 'CB'],
  rhydon: ['OL', 'DL'],
  arcanine: ['OL', 'DL', 'QB', 'S', 'WR', 'CB', 'RB', 'LB', 'TE'],
  ninetales: ['WR', 'CB'],
  fearow: ['WR', 'CB', 'RB', 'LB'],
  hitmonlee: ['RB', 'LB', 'TE'],
  victreebel: ['TE', 'LB'],
  kabutops: ['OL', 'DL', 'RB', 'LB', 'TE'],
  sandslash: ['OL', 'DL'],
  raticate: ['WR', 'CB'],
  nidoking: ['RB', 'LB', 'TE'],
  raichu: ['QB', 'S', 'WR', 'CB', 'RB', 'LB'],
  weezing: ['OL', 'DL', 'TE', 'LB'],
  flareon: ['OL', 'DL', 'TE', 'LB'],
  kingler: ['OL', 'DL', 'RB', 'LB'],
  dodrio: ['OL', 'DL', 'WR', 'CB', 'RB', 'LB'],
  pinsir: ['OL', 'DL', 'RB', 'LB', 'TE'],
  clefable: ['TE', 'LB'],
  goldeen: ['RB', 'LB'],
  venusaur: ['QB', 'S', 'TE', 'LB'],
  primeape: ['WR', 'CB', 'RB', 'LB', 'TE'],
};

/**
 * The preset opponents Quick Play's Match tab offers (slice 11), and harness 7 plays, the default
 * first (Team Electabuzz since slice 10a; Team Raichu re-picked in slices 10b and 10c). Since 10c
 * the AI picks which one it fields against the player's roster (sim ai/pick.ts).
 */
export type PresetId = 'raichu' | 'electabuzz';
export const PRESETS: readonly PresetId[] = ['electabuzz', 'raichu'];

/** Each preset's eleven in pick order (roster order), as creature ids. */
export const PRESET_SPECIES: Record<PresetId, readonly string[]> = {
  electabuzz: [
    'electabuzz',
    'rhydon',
    'arcanine',
    'ninetales',
    'fearow',
    'hitmonlee',
    'victreebel',
    'kabutops',
    'sandslash',
    'raticate',
    'nidoking',
  ],
  raichu: [
    'raichu',
    'weezing',
    'flareon',
    'kingler',
    'raticate',
    'dodrio',
    'pinsir',
    'clefable',
    'goldeen',
    'venusaur',
    'primeape',
  ],
};

/** The Pokémon a preset fields that the plain balanced teams do not (slice 10b), unkitted. */
const PRESET_ONLY: readonly Combatant[] = [
  mon('Weezing', ['Poison'], [65, 90, 120, 85, 70, 60], 'BENCH'),
  mon('Kingler', ['Water'], [55, 130, 115, 50, 50, 75], 'BENCH'),
  mon('Pinsir', ['Bug'], [65, 125, 100, 55, 70, 85], 'BENCH'),
  mon('Clefable', ['Fairy'], [95, 70, 73, 95, 90, 60], 'BENCH'),
  mon('Goldeen', ['Water'], [45, 67, 60, 35, 50, 63], 'BENCH'),
  mon('Venusaur', ['Grass', 'Poison'], [80, 82, 83, 100, 100, 80], 'BENCH'),
];

/** A preset's eleven in pick order, unkitted, from the plain teams' Pokémon and `PRESET_ONLY`. */
function presetRoster(id: PresetId): Combatant[] {
  const plain = balancedTeams();
  const all = [...plain.A.roster, ...plain.B.roster, ...PRESET_ONLY];
  return PRESET_SPECIES[id].map((s) => {
    const c = all.find((x) => x.creatureId === s);
    if (!c) throw new Error(`${s} has no fixture entry`);
    return c;
  });
}

/** A preset as the player's opponent: the kitted fixture team, always team B. */
export function presetTeam(id: PresetId): Team {
  const kitted = kittedBalancedTeams();
  return id === 'raichu' ? kitted.B : { ...kitted.A, id: 'B' };
}

/** The bag each kitted team packs: balance.status.itemBagPerGame items. */
export const KIT_BAG: readonly string[] = ['potion', 'superpotion', 'fullheal'];

/** Every move a kitted Pokémon knows, as the Gen 1 pack has it. */
// prettier-ignore
export const KIT_MOVES: Record<string, Move> = {
  agility: {id: 'agility', name: 'Agility', type: 'Psychic', category: 'Status', power: 0, accuracy: 1, pp: 6, priority: 0, effect: {kind: 'stage', stat: 'spe', stages: 2}},
  amnesia: {id: 'amnesia', name: 'Amnesia', type: 'Psychic', category: 'Status', power: 0, accuracy: 1, pp: 4, priority: 0, effect: {kind: 'stage', stat: 'spd', stages: 2}},
  aquajet: {id: 'aquajet', name: 'Aqua Jet', type: 'Water', category: 'Physical', power: 40, accuracy: 1, pp: 4, priority: 1, effect: {kind: 'burst', speedStages: 1}},
  aquatail: {id: 'aquatail', name: 'Aqua Tail', type: 'Water', category: 'Physical', power: 90, accuracy: 0.9, pp: 2, priority: 0, effect: {kind: 'attack', category: 'physical', power: 90, accuracy: 0.9, type: 'Water', contact: true}},
  barrier: {id: 'barrier', name: 'Barrier', type: 'Psychic', category: 'Status', power: 0, accuracy: 1, pp: 4, priority: 0, effect: {kind: 'stage', stat: 'def', stages: 2}},
  bulkup: {id: 'bulkup', name: 'Bulk Up', type: 'Fighting', category: 'Status', power: 0, accuracy: 1, pp: 4, priority: 0, effect: {kind: 'stage', stat: 'atk', stages: 1}},
  bulletpunch: {id: 'bulletpunch', name: 'Bullet Punch', type: 'Steel', category: 'Physical', power: 40, accuracy: 1, pp: 6, priority: 1, effect: {kind: 'burst', speedStages: 1}},
  closecombat: {id: 'closecombat', name: 'Close Combat', type: 'Fighting', category: 'Physical', power: 120, accuracy: 1, pp: 1, priority: 0, effect: {kind: 'attack', category: 'physical', power: 120, accuracy: 1, type: 'Fighting', contact: true}},
  crabhammer: {id: 'crabhammer', name: 'Crabhammer', type: 'Water', category: 'Physical', power: 100, accuracy: 0.9, pp: 2, priority: 0, effect: {kind: 'attack', category: 'physical', power: 100, accuracy: 0.9, type: 'Water', contact: true}},
  dig: {id: 'dig', name: 'Dig', type: 'Ground', category: 'Physical', power: 80, accuracy: 1, pp: 2, priority: 0, effect: {kind: 'attack', category: 'physical', power: 80, accuracy: 1, type: 'Ground', contact: true}},
  drillrun: {id: 'drillrun', name: 'Drill Run', type: 'Ground', category: 'Physical', power: 80, accuracy: 0.95, pp: 2, priority: 0, effect: {kind: 'attack', category: 'physical', power: 80, accuracy: 0.95, type: 'Ground', contact: true}},
  earthpower: {id: 'earthpower', name: 'Earth Power', type: 'Ground', category: 'Special', power: 90, accuracy: 1, pp: 2, priority: 0, effect: {kind: 'attack', category: 'special', power: 90, accuracy: 1, type: 'Ground'}},
  earthquake: {id: 'earthquake', name: 'Earthquake', type: 'Ground', category: 'Physical', power: 100, accuracy: 1, pp: 2, priority: 0, effect: {kind: 'attack', category: 'physical', power: 100, accuracy: 1, type: 'Ground'}},
  extremespeed: {id: 'extremespeed', name: 'Extreme Speed', type: 'Normal', category: 'Physical', power: 80, accuracy: 1, pp: 1, priority: 2, effect: {kind: 'burst', speedStages: 1}},
  feint: {id: 'feint', name: 'Feint', type: 'Normal', category: 'Physical', power: 30, accuracy: 1, pp: 2, priority: 2, effect: {kind: 'burst', speedStages: 1}},
  flareblitz: {id: 'flareblitz', name: 'Flare Blitz', type: 'Fire', category: 'Physical', power: 120, accuracy: 1, pp: 3, priority: 0, effect: {kind: 'attack', category: 'physical', power: 120, accuracy: 1, type: 'Fire', contact: true, recoil: 0.33}},
  focusblast: {id: 'focusblast', name: 'Focus Blast', type: 'Fighting', category: 'Special', power: 120, accuracy: 0.7, pp: 1, priority: 0, effect: {kind: 'attack', category: 'special', power: 120, accuracy: 0.7, type: 'Fighting'}},
  focuspunch: {id: 'focuspunch', name: 'Focus Punch', type: 'Fighting', category: 'Physical', power: 150, accuracy: 1, pp: 4, priority: -3, effect: {kind: 'attack', category: 'physical', power: 150, accuracy: 1, type: 'Fighting', contact: true}},
  frenzyplant: {id: 'frenzyplant', name: 'Frenzy Plant', type: 'Grass', category: 'Special', power: 150, accuracy: 0.9, pp: 1, priority: 0, effect: {kind: 'attack', category: 'special', power: 150, accuracy: 0.9, type: 'Grass'}},
  gigaimpact: {id: 'gigaimpact', name: 'Giga Impact', type: 'Normal', category: 'Physical', power: 150, accuracy: 0.9, pp: 1, priority: 0, effect: {kind: 'attack', category: 'physical', power: 150, accuracy: 0.9, type: 'Normal', contact: true}},
  gunkshot: {id: 'gunkshot', name: 'Gunk Shot', type: 'Poison', category: 'Physical', power: 120, accuracy: 0.8, pp: 1, priority: 0, effect: {kind: 'attack', category: 'physical', power: 120, accuracy: 0.8, type: 'Poison'}},
  honeclaws: {id: 'honeclaws', name: 'Hone Claws', type: 'Dark', category: 'Status', power: 0, accuracy: 1, pp: 3, priority: 0, effect: {kind: 'stage', stat: 'atk', stages: 1}},
  jumpkick: {id: 'jumpkick', name: 'Jump Kick', type: 'Fighting', category: 'Physical', power: 100, accuracy: 0.95, pp: 2, priority: 0, effect: {kind: 'attack', category: 'physical', power: 100, accuracy: 0.95, type: 'Fighting', contact: true}},
  lastresort: {id: 'lastresort', name: 'Last Resort', type: 'Normal', category: 'Physical', power: 140, accuracy: 1, pp: 1, priority: 0, effect: {kind: 'attack', category: 'physical', power: 140, accuracy: 1, type: 'Normal', contact: true}},
  leechlife: {id: 'leechlife', name: 'Leech Life', type: 'Bug', category: 'Physical', power: 80, accuracy: 1, pp: 2, priority: 0, effect: {kind: 'attack', category: 'physical', power: 80, accuracy: 1, type: 'Bug', contact: true, drain: 0.5}},
  liquidation: {id: 'liquidation', name: 'Liquidation', type: 'Water', category: 'Physical', power: 85, accuracy: 1, pp: 2, priority: 0, effect: {kind: 'attack', category: 'physical', power: 85, accuracy: 1, type: 'Water', contact: true}},
  moonblast: {id: 'moonblast', name: 'Moonblast', type: 'Fairy', category: 'Special', power: 95, accuracy: 1, pp: 3, priority: 0, effect: {kind: 'attack', category: 'special', power: 95, accuracy: 1, type: 'Fairy'}},
  overheat: {id: 'overheat', name: 'Overheat', type: 'Fire', category: 'Special', power: 130, accuracy: 0.9, pp: 1, priority: 0, effect: {kind: 'attack', category: 'special', power: 130, accuracy: 0.9, type: 'Fire'}},
  powerwhip: {id: 'powerwhip', name: 'Power Whip', type: 'Grass', category: 'Physical', power: 120, accuracy: 0.85, pp: 2, priority: 0, effect: {kind: 'attack', category: 'physical', power: 120, accuracy: 0.85, type: 'Grass', contact: true}},
  quickattack: {id: 'quickattack', name: 'Quick Attack', type: 'Normal', category: 'Physical', power: 40, accuracy: 1, pp: 6, priority: 1, effect: {kind: 'burst', speedStages: 1}},
  ragingfury: {id: 'ragingfury', name: 'Raging Fury', type: 'Fire', category: 'Physical', power: 120, accuracy: 1, pp: 2, priority: 0, effect: {kind: 'attack', category: 'physical', power: 120, accuracy: 1, type: 'Fire'}},
  rollout: {id: 'rollout', name: 'Rollout', type: 'Rock', category: 'Physical', power: 30, accuracy: 0.9, pp: 4, priority: 0, effect: {kind: 'attack', category: 'physical', power: 30, accuracy: 0.9, type: 'Rock', contact: true}},
  scorchingsands: {id: 'scorchingsands', name: 'Scorching Sands', type: 'Ground', category: 'Special', power: 70, accuracy: 1, pp: 2, priority: 0, effect: {kind: 'attack', category: 'special', power: 70, accuracy: 1, type: 'Ground'}},
  suckerpunch: {id: 'suckerpunch', name: 'Sucker Punch', type: 'Dark', category: 'Physical', power: 70, accuracy: 1, pp: 1, priority: 1, effect: {kind: 'burst', speedStages: 1}},
  superpower: {id: 'superpower', name: 'Superpower', type: 'Fighting', category: 'Physical', power: 120, accuracy: 1, pp: 1, priority: 0, effect: {kind: 'attack', category: 'physical', power: 120, accuracy: 1, type: 'Fighting', contact: true}},
  swagger: {id: 'swagger', name: 'Swagger', type: 'Normal', category: 'Status', power: 0, accuracy: 0.85, pp: 3, priority: 0, effect: {kind: 'status', status: 'confusion', accuracy: 0.85}},
  swordsdance: {id: 'swordsdance', name: 'Swords Dance', type: 'Normal', category: 'Status', power: 0, accuracy: 1, pp: 4, priority: 0, effect: {kind: 'stage', stat: 'atk', stages: 2}},
  thunderbolt: {id: 'thunderbolt', name: 'Thunderbolt', type: 'Electric', category: 'Special', power: 90, accuracy: 1, pp: 3, priority: 0, effect: {kind: 'attack', category: 'special', power: 90, accuracy: 1, type: 'Electric'}},
  vacuumwave: {id: 'vacuumwave', name: 'Vacuum Wave', type: 'Fighting', category: 'Special', power: 40, accuracy: 1, pp: 6, priority: 1, effect: {kind: 'burst', speedStages: 1}},
  volttackle: {id: 'volttackle', name: 'Volt Tackle', type: 'Electric', category: 'Physical', power: 120, accuracy: 1, pp: 3, priority: 0, effect: {kind: 'attack', category: 'physical', power: 120, accuracy: 1, type: 'Electric', contact: true, recoil: 0.33}},
  workup: {id: 'workup', name: 'Work Up', type: 'Normal', category: 'Status', power: 0, accuracy: 1, pp: 6, priority: 0, effect: {kind: 'stage', stat: 'atk', stages: 1}},
  xscissor: {id: 'xscissor', name: 'X-Scissor', type: 'Bug', category: 'Physical', power: 80, accuracy: 1, pp: 3, priority: 0, effect: {kind: 'attack', category: 'physical', power: 80, accuracy: 1, type: 'Bug', contact: true}},
};

/** Each species' drafted move set, by creature id. */
export const KIT: Record<string, string[]> = {
  electabuzz: ['thunderbolt', 'focusblast', 'barrier', 'feint'],
  rhydon: ['earthquake', 'focuspunch', 'swordsdance'],
  arcanine: ['ragingfury', 'closecombat', 'agility', 'extremespeed'],
  ninetales: ['overheat', 'scorchingsands', 'agility', 'quickattack'],
  fearow: ['gigaimpact', 'drillrun', 'workup', 'quickattack'],
  hitmonlee: ['focuspunch', 'earthquake', 'swordsdance', 'bulletpunch'],
  victreebel: ['powerwhip', 'leechlife', 'swordsdance', 'suckerpunch'],
  kabutops: ['liquidation', 'superpower', 'swordsdance', 'aquajet'],
  sandslash: ['earthquake', 'focuspunch', 'swordsdance'],
  raticate: ['lastresort', 'dig', 'swordsdance', 'quickattack'],
  nidoking: ['earthquake', 'focuspunch', 'honeclaws', 'suckerpunch'],
  raichu: ['volttackle', 'focuspunch', 'agility', 'extremespeed'],
  weezing: ['gunkshot', 'rollout', 'swagger'],
  flareon: ['flareblitz', 'superpower', 'workup', 'quickattack'],
  kingler: ['crabhammer', 'superpower', 'swordsdance'],
  dodrio: ['gigaimpact', 'jumpkick', 'agility', 'quickattack'],
  pinsir: ['xscissor', 'focuspunch', 'swordsdance', 'feint'],
  clefable: ['moonblast', 'focusblast', 'workup'],
  goldeen: ['aquatail', 'drillrun', 'swordsdance', 'quickattack'],
  venusaur: ['frenzyplant', 'earthpower', 'amnesia'],
  primeape: ['focuspunch', 'earthquake', 'bulkup', 'vacuumwave'],
};
